import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6"  x2="21" y2="6"  />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6"  x2="6"  y2="18" />
    <line x1="6"  y1="6"  x2="18" y2="18" />
  </svg>
);

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="none">
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/>
  </svg>
);

const NAV_LINKS = [
  { label: "Discover",       href: "/#discover" },
  { label: "For Businesses", href: "/#for-business" },
  { label: "FAQ",            href: "/#faq" },
  { label: "Contact Us",     href: "/contact" },
];

export function LandingNavbar({ theme, toggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLinkClick = (e, href) => {
    if (href.startsWith("/#")) {
      e.preventDefault();
      const id = href.replace("/#", "");
      if (location.pathname === "/") {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          window.history.pushState(null, null, href);
        }
      } else {
        navigate(href);
      }
    } else if (href.startsWith("/")) {
      e.preventDefault();
      if (location.pathname === href) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate(href);
      }
    }
    setOpen(false);
  };

  const navStyle = {
    background: scrolled ? "var(--lp-color-bg-w90)" : "transparent",
    borderBottom: scrolled ? "1px solid var(--lp-color-border)" : "none",
  };

  return (
    <nav
      className="lp-fixed lp-top-0 lp-left-0 lp-right-0 lp-z-50 lp-transition"
      style={navStyle}
    >
      {scrolled && <div className="lp-blur lp-absolute lp-inset-0 lp-pointer-none" style={{ zIndex: -1 }} />}

      <div className="lp-max-6xl lp-mx-auto lp-px-6 lp-h-16 lp-flex lp-items-center lp-justify-between">
        {/* Logo */}
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <img
            src="/assets/images/logo-light.png"
            alt="Hubscroll"
            style={{ height: "2.5rem", display: "block" }}
            onError={(e) => { e.target.onerror = null; e.target.src = "/assets/images/logo.png"; }}
          />
          <span style={{
            background: "linear-gradient(270deg, #24c5f7 0%, #0388d6 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-block",
          }}>
            Hubscroll
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="lp-hidden md-flex lp-items-center lp-gap-8">
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              onClick={(e) => handleLinkClick(e, href)}
              className="lp-text-sm lp-text-muted lp-transition-colors lp-hov-dark"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
            >
              {label}
            </a>
          ))}
          
          {/* Modern Pill Theme Toggle - Desktop */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
            style={{
              background: theme === "dark" ? "rgba(74,158,255,0.15)" : "rgba(0,0,0,0.07)",
              border: theme === "dark" ? "1px solid rgba(74,158,255,0.3)" : "1px solid rgba(0,0,0,0.12)",
              borderRadius: "9999px",
              cursor: "pointer",
              padding: "0.3rem 0.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.35rem",
              transition: "all 0.3s ease",
            }}
          >
            <span style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              background: theme === "dark" ? "transparent" : "#fff",
              boxShadow: theme === "dark" ? "none" : "0 1px 4px rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
              color: theme === "dark" ? "rgba(255,255,255,0.4)" : "#f59e0b",
            }}>
              <SunIcon />
            </span>
            <span style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              background: theme === "dark" ? "#4A9EFF" : "transparent",
              boxShadow: theme === "dark" ? "0 0 8px rgba(74,158,255,0.5)" : "none",
              transition: "all 0.3s ease",
              color: theme === "dark" ? "#fff" : "rgba(0,0,0,0.3)",
            }}>
              <MoonIcon />
            </span>
          </button>

          <a
            href="#download"
            onClick={(e) => handleLinkClick(e, "/#download")}
            className="lp-text-sm lp-px-5 lp-py-2 lp-r-full lp-bg-blue lp-text-white lp-transition lp-hov-bg-blue-lt"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
          >
            Download App
          </a>
        </div>

        {/* Mobile burger */}
        <button
          className="md-hidden"
          onClick={() => setOpen(!open)}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--lp-color-text-dark)" }}
        >
          {open ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md-hidden lp-bg-w95 lp-blur lp-border-t lp-bc-b08 lp-px-6 lp-py-6 lp-flex lp-flex-col lp-gap-5"
        >
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              onClick={(e) => handleLinkClick(e, href)}
              className="lp-text-muted lp-transition-colors lp-hov-dark"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {label}
            </a>
          ))}
          
          {/* Mobile Modern Pill Theme Toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "none",
              border: "1px solid var(--lp-color-border)",
              borderRadius: "9999px",
              cursor: "pointer",
              padding: "0.4rem 0.75rem",
              color: "var(--lp-color-text-dark)",
              fontFamily: "'Outfit', sans-serif",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "all 0.2s ease",
            }}
          >
            {theme === "light" ? <MoonIcon /> : <SunIcon />}
            <span>{theme === "light" ? "Dark Mode" : "Light Mode"}</span>
          </button>

          <a
            href="#download"
            onClick={(e) => handleLinkClick(e, "/#download")}
            className="lp-text-sm lp-px-5 lp-py-2 lp-r-full lp-bg-blue lp-text-white lp-text-center"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
          >
            Download App
          </a>
        </div>
      )}
    </nav>
  );
}
